/**
 * Client and types for the TMR authorization backend (`/api/v1/auth/me/*`).
 *
 * This is the Phase 3 replacement for reading permissions from Clerk Organizations: the
 * backend now owns roles, permissions and the Empresa/Holding scope, keyed by the Clerk
 * `sub`. It is gated behind the `VITE_AUTHZ_BACKEND` feature flag so it can run side by side
 * with the legacy Organizations path (flag off = legacy, no behavior change).
 *
 * The browser sends the Clerk session JWT as a Bearer token; the backend validates it (JWKS).
 */
import { runtimeConfig } from './runtimeConfig';

export type ActiveContextType = 'all' | 'holding' | 'empresa';

export interface ActiveContext {
  type: ActiveContextType;
  id: string | null;
}

export interface AuthorizationScope {
  /** True when the user can reach every Empresa (unscoped). */
  allEmpresas: boolean;
  /** Holdings the user has consolidated access to. */
  holdingIds: string[];
  /** Count of reachable Empresas — never the list itself (it can reach the millions). */
  empresaCount: number;
}

/**
 * What this deployment is wired for, as opposed to what this operator may do.
 *
 * It rides along with the authorization payload because the portal already reads that once per session —
 * a separate endpoint would be a second round-trip for one boolean. A screen uses it to *state* a fact
 * instead of inferring one from a status code: a 404 is also what a bad ingress rule, a wrong base path
 * or a half-finished deploy look like.
 */
export interface EnvironmentCapabilities {
  /**
   * Whether the access-approval surface exists. False when the backend has no Clerk secret key, in which
   * case those routes are not mapped at all and the queue cannot be synced or decided (10 B7d).
   */
  accessRequests: boolean;
  /**
   * Whether a staff login can be created in the legacy back-office from the Users screen (33). False when
   * the backend has no legacy connection, in which case those routes are not mapped at all. An older backend
   * does not send it, which reads as false — the safe answer.
   */
  legacyBackofficeAccess?: boolean;
}

export interface ResolvedAuthorization {
  roles: string[];
  permissions: string[];
  scope: AuthorizationScope;
  activeContext: ActiveContext | null;
  capabilities: EnvironmentCapabilities;
}

export interface EmpresaSummary {
  id: string;
  nome: string;
  holdingId: string | null;
}

export interface EmpresasPage {
  items: EmpresaSummary[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** A function returning the current Clerk session JWT (or null when signed out). */
export type TokenGetter = () => Promise<string | null>;

/** `true` when backend-driven authorization is enabled; otherwise the legacy Organizations path runs. */
export function authzBackendEnabled(): boolean {
  return runtimeConfig('VITE_AUTHZ_BACKEND') === 'true';
}

/**
 * Base URL for the TMR API. Defaults to same-origin (proxied by Vite in dev, one Ingress host in the
 * cluster), so the httpOnly session cookie is first-party and no CORS is involved.
 */
function apiBase(): string {
  return (runtimeConfig('VITE_AUTHZ_API_URL') ?? '').replace(/\/+$/, '');
}

const SESSION_PATH = '/api/v1/auth/session';
const ME_PATH = '/api/v1/auth/me';

/** Thrown when a TMR API call returns a non-OK status. */
export class AuthorizationError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AuthorizationError';
  }
}

/** Identity confirmed by the session exchange; the session token itself stays in the httpOnly cookie. */
export interface SessionIdentity {
  userId: string;
  email: string;
  name: string;
}

/**
 * Exchanges the Clerk session JWT for the TMR session cookie (05 §3). The Clerk Bearer is presented
 * exactly once — here; every later call is cookie-based and the browser holds no token. Must run before
 * the /me/* calls, which authenticate via the cookie.
 */
export async function exchangeClerkSession(getToken: TokenGetter): Promise<SessionIdentity> {
  const token = await getToken();
  if (!token) {
    throw new AuthorizationError(401, 'Não há uma identidade do Clerk para trocar por uma sessão.');
  }
  const response = await fetch(`${apiBase()}${SESSION_PATH}`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw await toExchangeError(response);
  }
  return (await response.json()) as SessionIdentity;
}

/**
 * Turns a refused exchange into an error carrying the backend's own reason.
 *
 * The body used to be thrown away, and the screen showed a bare status code — so the single most common
 * cause, a Clerk token with no `email` claim, reached the operator as "(400)" and nothing else. The
 * backend already answers ProblemDetails with an operator-facing PT-BR `detail`; this is only about not
 * discarding it.
 */
async function toExchangeError(response: Response): Promise<AuthorizationError> {
  try {
    const problem = (await response.json()) as { detail?: string };
    const detail = problem.detail?.trim();
    if (detail) return new AuthorizationError(response.status, detail);
  } catch {
    // A failure with no JSON body (a proxy error page, a network-level 502) still has to become an error.
  }
  return new AuthorizationError(response.status, `Não foi possível abrir a sessão (${response.status}).`);
}

/** Cookie-based request against a /me/* endpoint — the TMR session cookie is sent automatically. */
async function meRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  return fetch(`${apiBase()}${path}`, { ...init, credentials: 'include', headers });
}

/** GET /me/authorization — the small, cacheable payload read on sign-in/refresh (cookie-authenticated). */
export async function fetchAuthorization(): Promise<ResolvedAuthorization> {
  const response = await meRequest(`${ME_PATH}/authorization`);
  if (!response.ok) {
    throw new AuthorizationError(response.status, `Failed to load authorization (${response.status}).`);
  }
  return (await response.json()) as ResolvedAuthorization;
}

/** GET /me/empresas — paginated/searchable, used only by the context selector. */
export async function fetchEmpresas(
  params: { search?: string; page?: number; pageSize?: number } = {},
): Promise<EmpresasPage> {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  const response = await meRequest(`${ME_PATH}/empresas${qs ? `?${qs}` : ''}`);
  if (!response.ok) {
    throw new AuthorizationError(response.status, `Failed to load empresas (${response.status}).`);
  }
  return (await response.json()) as EmpresasPage;
}

/** PUT /me/active-context — validated server-side against the resolved scope. */
export async function setActiveContext(context: ActiveContext): Promise<void> {
  const response = await meRequest(`${ME_PATH}/active-context`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(context),
  });
  if (!response.ok) {
    throw new AuthorizationError(response.status, `Failed to set active context (${response.status}).`);
  }
}

/**
 * Portal access permissions when sourced from the backend — dotted keys, no `org:` prefix
 * (e.g. `appointments.reader`). The legacy `TMR_PORTAL_PERMISSIONS` (`org:` keys) stays until
 * the Organizations path is removed (Phase 5).
 */
export const TMR_PORTAL_PERMISSIONS_BACKEND: Record<string, string[]> = {
  paciente: ['appointments.reader'],
  // Entry to the Portal da Empresa — the Clínica's portal — is its own key, seeded by the
  // `Identity_PortalCompanyAccessPermission` migration. What stood here was `appointments.reader`, a
  // placeholder from the Organizations days that the backend has never issued: with the backend path on,
  // the gate measured the session against a key that cannot exist, so the portal refused everyone and
  // said nothing about why. The migration grants it to no role, so access starts closed by design and is
  // handed out per role in the Portal do Administrador.
  empresa: ['portal.empresa.access'],
  cobranca: ['appointments.reader'],
  // Entry to the admin portal is its own permission, not authorization.admin. The two were one key until
  // 2026-07-28, which meant nobody could use the portal without also being able to create roles and
  // approve access — so an invited operator either got in as a full authorization administrator or did not
  // get in at all. Everything inside the portal is still gated separately (users.manage, company.read, …),
  // so this key alone gets someone to a shell that shows them only what they were granted.
  //
  // The migration that seeds it grants it to every role already holding authorization.admin, so changing
  // this line locks nobody out — but only if that migration ran first (06 §6.1).
  admin: ['portal.admin.access'],
};
